import React, { useRef, useEffect, useState } from 'react'

import { gsap } from 'gsap'

import { CSSPlugin } from 'gsap/CSSPlugin'
import { Link } from 'gatsby'

gsap.registerPlugin(CSSPlugin)

const YellowButton = ({ text, click, link, isExternal, classes, keypress, type, disabled = false }) => {
    const [hovered, setHovered] = useState(false)
    const buttonRef = useRef(null)
    const orangeBarRef = useRef(null)
    const btnSwipe = useRef(null)
    const greyBarRef = useRef(null)
    const grayLine = useRef(null)
    const graySquares = useRef(null)
    const leftEdge = useRef(null)
    const rightEdge = useRef(null)

    const mouseEnter = () => setHovered(true)
    const mouseLeave = () => setHovered(false)

    const tl = useRef(
        gsap
            .timeline()
            .addLabel('swiped', 0.2)
            .addLabel('completed', 0.5)
            .reverse()
    )

    useEffect(() => {
        tl.current.reversed(!hovered)
    }, [hovered])

    useEffect(() => {
        tl.current.to([btnSwipe.current], {
            left: 0,
            duration: 0.2,
        })

        tl.current.to(
            [greyBarRef.current],
            {
                width: '85%',
                duration: 0.2,
            },
            'swiped'
        )

        tl.current.to(
            [graySquares.current.children],
            {
                padding: '4px',
                duration: 0.1,
                stagger: 0.05,
            },
            'swiped'
        )

        tl.current.to(
            [grayLine.current],
            {
                height: '32px',
                duration: 0.1,
            },
            'swiped'
        )

        tl.current.to(
            [leftEdge.current],
            {
                borderTopWidth: '2px',
                borderRightWidth: '2px',
                width: '16px',
                height: '16px',
                duration: 0.1,
            },
            'completed'
        )

        tl.current.to(
            [rightEdge.current],
            {
                borderBottomWidth: '2px',
                borderLeftWidth: '2px',
                width: '16px',
                height: '16px',
                duration: 0.1,
            },
            'completed'
        )
    }, [])

    const renderLink = (isExternal, link) => {
        if (!isExternal && link) {
            return (
                <Link to={link} className="z-50">
                    <div className="py-4 px-14 z-50">
                        <p
                            className={`text-darks5 uppercase z-50 font-body text-xl tracking-wider whitespace-nowrap`}
                        >
                            {text}
                        </p>
                    </div>
                </Link>
            )
        } else if (isExternal && link) {
            return (
                <a
                    href={link}
                    className="z-50"
                    target="_blank"
                    rel="noreferrer"
                >
                    <div className="py-4 px-14 z-50">
                        <p
                            className={`text-darks5 uppercase z-50 font-body text-xl tracking-wider whitespace-nowrap`}
                        >
                            {text}
                        </p>
                    </div>
                </a>
            )
        } else if (type === "submit") {
            return (
                <button className="py-4 px-14 z-50 text-darks5 uppercase font-body text-xl tracking-wider" type={type} disabled={disabled}>
                    {text}
                </button>
            )
        } else {
            return (
                <div className="py-4 px-14 z-50">
                    <p
                        className={`text-darks5 uppercase z-50 font-body text-xl tracking-wider whitespace-nowrap`}
                    >
                        {text}
                    </p>
                </div>
            )
        }
    }

    return (
        <div
            ref={buttonRef}
            className={`cursor-pointer relative w-fit whitespace-nowrap ${classes ? classes : null
                }`}
            onMouseEnter={!disabled ? mouseEnter : null}
            onMouseLeave={!disabled ? mouseLeave : null}
            onClick={click && !disabled ? () => click() : undefined}
            onKeyPress={keypress && !disabled ? (e) => (e.key === "Enter") && keypress() : undefined}
            role="button"
            tabIndex={0}
        >
            <div className={`button-bg flex items-center justify-center clipped-input relative ${!disabled ? 'bg-yellow' : 'bg-darks1'}`}>
                <span
                    className="block w-[120%] h-full bg-white z-10 absolute top-0 left-[-150%] clipped-btn-swipe"
                    ref={btnSwipe}
                />
                {renderLink(isExternal, link)}
                <div
                    className={`top-bar absolute top-0 left-0 w-[85%] h-[6px] ${!disabled ? 'bg-orange' : 'bg-darks4'}`}
                    ref={orangeBarRef}
                />
                <div
                    className="bg-gray absolute top-0 left-0 w-[0%] z-20 h-[6px]"
                    ref={greyBarRef}
                />

                <div className="squares absolute top-[40%] left-3">
                    <div className="relative grid grid-cols-2">
                        <div className={` p-[4px] ${!disabled ? 'bg-orange' : 'bg-darks4'}`} />
                        <div className="transparent p-[4px]" />
                        <div className="transparent p-[4px]" />
                        <div className={` p-[4px] ${!disabled ? 'bg-orange' : 'bg-darks4'}`} />
                        <div className={` p-[4px] ${!disabled ? 'bg-orange' : 'bg-darks4'}`} />
                        <div className="transparent p-[4px]" />
                    </div>
                </div>

                <div className="squares absolute top-[40%] z-20 left-3">
                    <div
                        className="relative grid grid-cols-2"
                        ref={graySquares}
                    >
                        <div className="bg-gray p-[0px]" />
                        <div className="transparent p-[0px]" />
                        <div className="transparent p-[0px]" />
                        <div className="bg-gray p-[0px]" />
                        <div className="bg-gray p-[0px]" />
                        <div className="transparent p-[0px]" />
                    </div>
                </div>

                <div className={`w-[1px] h-8 right-3 top-3 absolute ${!disabled ? 'bg-orange' : 'bg-darks4'}`} />
                <div
                    className="bg-gray w-[1px] h-0 right-3 top-3 absolute z-20"
                    ref={grayLine}
                />
            </div>

            <div
                className="border-t-0 border-r-0 border-gray absolute -top-2 -right-2"
                ref={leftEdge}
            />
            <div
                className="border-b-0 border-l-0 border-gray absolute -bottom-2 -left-2"
                ref={rightEdge}
            />
        </div>
    )
}

export default YellowButton
